import {
  computed,
  inject,
  Injectable,
  signal
} from '@angular/core';

import { ThemeService } from '../../branding/services/theme.service';
import { DEFAULT_APP_CONFIG } from '../constants/default-app-config';
import { AppRuntimeConfig } from '../models/app-runtime-config.model';
import { parseAppConfig } from '../parsers/app-config.parser';
import { ConfigSourceService } from './config-source.service';

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  private readonly configSource =
    inject(ConfigSourceService);

  private readonly themeService =
    inject(ThemeService);

  private readonly configState =
    signal<AppRuntimeConfig>(
      DEFAULT_APP_CONFIG
    );

  readonly config =
    this.configState.asReadonly();

  readonly application = computed(
    () => this.configState().application
  );

  readonly layout = computed(
    () => this.configState().layout
  );

  readonly features = computed(
    () => this.configState().features
  );

  readonly theme = computed(
    () => this.configState().theme
  );

  readonly assets = computed(
    () => this.configState().assets
  );

  readonly tenant = computed(
    () => this.configState().tenant
  );

  async initialize(): Promise<void> {
    let effectiveConfig =
      DEFAULT_APP_CONFIG;

    try {
      const rawConfig =
        await this.configSource.load();

      effectiveConfig =
        parseAppConfig(rawConfig);
    } catch (error) {
      /*
       * No registrar el contenido completo de la configuración.
       * El contenido podría contener URLs o información institucional.
       */
      console.error(
        'No fue posible cargar la configuración de marca. Se utilizará la configuración predeterminada.',
        this.normalizeError(error)
      );
    }

    this.configState.set(
      effectiveConfig
    );

    this.themeService.apply(
      effectiveConfig
    );
  }

  isFeatureEnabled(
    feature: keyof AppRuntimeConfig['features']
  ): boolean {
    return this.configState().features[
      feature
    ];
  }

  private normalizeError(
    error: unknown
  ): string {
    return error instanceof Error
      ? error.message
      : 'Error desconocido';
  }
}
